// Word Search interface (UX guide §3.6): tap first letter then last (drag optional), snap to straight
// lines, reversed selections count, found = capsule overlay + strike in list + announcement.
import { lineBetween, matchWord, snapEnd, type Placement, type WLevel, type WordPuzzle } from "./core"
import { readJSON, writeJSON } from "../lib/store"
import { formatLong, isToday, mark } from "../lib/daily"
import { gameEvent } from "../lib/game-events"
import { gameProgress, newAttempt } from "../lib/stats"

export interface WordDaily { game: string; date: string }

interface Saved { v: 1; puzzle: WordPuzzle; found: string[]; attempt?: number; cleared?: Cleared | null }
/** Words found before a Restart, with that attempt's number, so Undo brings back exactly that game. */
interface Cleared { found: string[]; attempt: number }
const PREF_KEY = "dt:pref:word-search-level"


export class WordSearchGame {
  private p: WordPuzzle
  private found: string[] = []
  private cleared: Cleared | null = null
  private attempt = 0 // Restarts of this puzzle; part of the stats clock id
  private tried = false // any selection made: the clock starts with the search, not the first word found
  private start: [number, number] | null = null
  private dragFrom: [number, number] | null = null
  private dragged = false
  private dragId = -1 // the finger or mouse that started the drag; another one lifting doesn't end it
  private focus: [number, number] = [0, 0]
  private hinted: string | null = null
  private hints = 0
  private sentFirst = false
  private undos = 0
  private readonly status = document.querySelector<HTMLElement>("[data-status]")
  private readonly table: HTMLTableElement
  private readonly overlay: SVGSVGElement

  private readonly saveKey: (l: WLevel) => string
  private readonly bank: string

  constructor(private readonly root: HTMLElement, private readonly daily?: WordDaily) {
    // A holiday page (data-bank="halloween") plays its own bank and keeps its own saves; the size choice is shared.
    this.bank = root.dataset.bank ? `word-search/${root.dataset.bank}` : "word-search"
    const own = root.dataset.bank ? `${root.dataset.bank}-` : ""
    this.saveKey = (l) => (daily ? `dt:save:daily:word-search:${daily.date}:v1` : `dt:save:word-search-${own}${l}:v1`)
    this.table = root.querySelector<HTMLTableElement>("[data-grid]")!
    this.overlay = root.querySelector<SVGSVGElement>("[data-overlay]")!
    const ssr = JSON.parse(root.dataset.puzzle ?? "{}") as WordPuzzle
    const pref = daily ? null : readPref()
    const saved = readJSON<Saved>(this.saveKey(pref ?? ssr.level))
    // A finished puzzle comes back too, so its "what next" card shows (lib/finish.ts) instead of a silent swap.
    if (saved?.v === 1 && saved.puzzle && (!daily || saved.puzzle.id === ssr.id)) {
      this.p = saved.puzzle
      this.found = saved.found
      this.attempt = saved.attempt ?? 0
      this.cleared = saved.cleared ?? null
      if (this.found.length < this.p.words.length) this.say("Welcome back. Your puzzle is where you left it.")
    } else if (pref && pref !== ssr.level) {
      this.p = ssr
      void this.loadLevel(pref)
    } else {
      this.p = ssr
      this.attempt = newAttempt()
    }
    if (daily) root.querySelector('[data-act="new"]')?.remove()
    document.querySelector("[data-ws-levels]")?.addEventListener("click", (e) => {
      const l = (e.target as HTMLElement).closest<HTMLElement>("[data-level]")?.dataset.level as WLevel | undefined
      if (l) void this.loadLevel(l)
    })
    root.addEventListener("pointerdown", (e) => this.onPointerDown(e))
    // On the window, so a drag released outside the grid still ends (and clears its highlight).
    addEventListener("pointerup", (e) => this.onPointerUp(e))
    addEventListener("pointercancel", (e) => {
      if (!this.dragFrom || e.pointerId !== this.dragId) return
      this.dragFrom = null; this.dragged = false; this.start = null
      this.preview(null, null)
      this.say("Selection canceled.")
    })
    root.addEventListener("pointermove", (e) => this.onPointerMove(e))
    root.addEventListener("keydown", (e) => this.onKey(e))
    root.addEventListener("click", (e) => this.onTool(e))
    if (typeof ResizeObserver === "function") new ResizeObserver(() => this.drawOverlay()).observe(this.table)
    this.rebuild()
  }

  // ---- puzzle lifecycle ------------------------------------------------------------------------
  private async loadLevel(level: WLevel, avoid?: string): Promise<void> {
    if (this.sentFirst && this.found.length < this.p.words.length) gameEvent({ type: "end", ruleset: `word-search:${this.p.level}`, outcome: "new_game", hints: this.hints, undos: this.undos, deal: this.p.id })
    this.sentFirst = false; this.hints = 0; this.undos = 0
    try {
      const saved = readJSON<Saved>(this.saveKey(level))
      if (saved?.v === 1 && saved.found.length < saved.puzzle.words.length && saved.puzzle.id !== avoid) {
        this.p = saved.puzzle; this.found = saved.found; this.attempt = saved.attempt ?? 0; this.cleared = saved.cleared ?? null
      } else {
        const bank = (await (await fetch(`/puzzles/${this.bank}/${level}.json`)).json()) as WordPuzzle[]
        const choices = bank.filter((b) => b.id !== avoid && b.id !== this.p.id)
        this.p = choices[Math.floor(Math.random() * choices.length)] ?? bank[0]!
        this.found = []
        this.attempt = newAttempt()
        this.cleared = null // a Restart's words belong to that puzzle only
      }
      writePref(level)
      this.start = null
      this.tried = false
      this.focus = [0, 0]
      this.rebuild()
      this.persist()
      this.say(`${cap(level)} puzzle: ${this.p.theme}. ${this.p.words.length} words to find.`)
    } catch {
      this.say("Couldn't load a puzzle. Check your connection and try again.")
    }
  }

  private persist(): void {
    writeJSON(this.saveKey(this.p.level), { v: 1, puzzle: this.p, found: this.found, attempt: this.attempt, cleared: this.cleared } satisfies Saved)
  }

  // ---- selection -------------------------------------------------------------------------------
  private cellFrom(e: Event): [number, number] | null {
    const target = e instanceof PointerEvent && e.pointerType !== "mouse" && e.type !== "pointerdown"
      ? (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null)
      : (e.target as HTMLElement)
    const td = target?.closest<HTMLElement>("td[data-r]")
    return td ? [Number(td.dataset.r), Number(td.dataset.c)] : null
  }

  private onPointerDown(e: PointerEvent): void {
    const cell = this.cellFrom(e)
    if (!cell) return
    e.preventDefault()
    if (this.dragFrom && e.pointerId !== this.dragId) return // a second finger while one is dragging
    this.dragFrom = cell
    this.dragId = e.pointerId
    this.focusCell(cell)
  }

  private onPointerMove(e: PointerEvent): void {
    if (!this.dragFrom || e.buttons === 0) return
    const cell = this.cellFrom(e)
    if (cell && (cell[0] !== this.dragFrom[0] || cell[1] !== this.dragFrom[1])) { this.dragged = true; this.preview(this.dragFrom, cell) }
  }

  private onPointerUp(e: PointerEvent): void {
    const from = this.dragFrom
    if (!from || e.pointerId !== this.dragId) return
    this.dragFrom = null
    const over = this.cellFrom(e)
    if (!over && this.dragged) { this.dragged = false; this.start = null; this.preview(null, null); return this.say("Selection canceled.") }
    this.dragged = false
    const cell = over ?? from
    if (cell[0] !== from[0] || cell[1] !== from[1]) { this.start = null; return this.submit(from, cell) } // a drag replaces a tapped start
    this.tap(cell)
  }

  private tap(cell: [number, number]): void {
    if (!this.start) {
      this.start = cell
      this.preview(cell, cell)
      return this.say(`Start: ${this.letter(cell)}, row ${cell[0] + 1}, column ${cell[1] + 1}. Choose the last letter.`)
    }
    if (this.start[0] === cell[0] && this.start[1] === cell[1]) {
      this.start = null
      this.preview(null, null)
      return this.say("Selection cleared.")
    }
    const from = this.start
    this.start = null
    this.submit(from, cell)
  }

  private submit(from: [number, number], to: [number, number]): void {
    const end = snapEnd(this.p.size, from[0], from[1], to[0], to[1])
    const cells = lineBetween(this.p.size, from[0], from[1], end[0], end[1])
    this.preview(null, null)
    this.tried = true
    const hit = cells ? matchWord(this.p, cells, new Set(this.found)) : null
    const again = !hit && cells ? matchWord(this.p, cells, new Set()) : null
    if (again) { this.renderState(); return this.say(`You already found ${again.w}.`) }
    if (!hit) { this.renderState(); return this.say(`${cells ? cells.map((c) => this.letter(c)).join("") : "That"} isn't one of the words. Try again.`) }
    this.found.push(hit.w)
    this.hinted = null
    this.persist()
    this.renderState()
    const left = this.p.words.length - this.found.length
    const ruleset = `word-search:${this.p.level}`
    if (!this.sentFirst) { this.sentFirst = true; gameEvent({ type: "first_move", ruleset, daily: !!this.daily }) }
    if (!left) gameEvent({ type: "end", ruleset, outcome: "win", hints: this.hints, undos: this.undos, deal: this.p.id, daily: !!this.daily })
    if (this.daily) mark(this.daily.game, this.daily.date, left ? "played" : "won")
    this.say(left ? `Found ${hit.w}. ${this.found.length} of ${this.p.words.length} words.` : this.daily ? (isToday(this.daily.date) ? `You found all ${this.p.words.length} words in today's puzzle! Come back tomorrow for a new one.` : `You found all ${this.p.words.length} words in the puzzle for ${formatLong(this.daily.date)}!`) : `You found all ${this.p.words.length} words! Choose New puzzle to play again.`)
  }

  // ---- keyboard ----------------------------------------------------------------------------
  private onKey(e: KeyboardEvent): void {
    if (!(e.target as HTMLElement).closest("td[data-r]")) return
    const [r, c] = this.focus
    const n = this.p.size
    const moves: Record<string, [number, number]> = {
      ArrowUp: [Math.max(0, r - 1), c], ArrowDown: [Math.min(n - 1, r + 1), c],
      ArrowLeft: [r, Math.max(0, c - 1)], ArrowRight: [r, Math.min(n - 1, c + 1)],
      Home: [r, 0], End: [r, n - 1],
    }
    const to = moves[e.key]
    if (to) {
      e.preventDefault()
      this.focusCell(to)
      if (this.start) this.preview(this.start, to)
      return
    }
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); return this.tap(this.focus) }
    if (e.key === "Escape") { this.start = null; this.preview(null, null); this.say("Selection cleared.") }
  }

  private onTool(e: Event): void {
    const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act
    if (act === "hint") this.hint()
    else if (act === "undo") this.undoWord()
    else if (act === "new") void this.loadLevel(this.p.level, this.p.id)
    else if (act === "restart") this.restart()
  }

  private hint(): void {
    const next = this.p.words.find((w) => !this.found.includes(w.w))
    if (!next) return this.say("You've found every word.")
    this.hints++
    this.hinted = next.w
    this.renderState()
    this.say(`Hint: ${next.w} starts at row ${next.r + 1}, column ${next.c + 1}, outlined on the grid.`)
  }

  /** This puzzle again with no words found. Undo brings them all back. */
  private restart(): void {
    if (!this.found.length) return this.say("No words found yet.")
    this.cleared = { found: this.found, attempt: this.attempt }
    this.found = []
    this.attempt = newAttempt()
    this.tried = false
    this.sentFirst = false; this.hints = 0; this.undos = 0
    this.persist()
    this.renderState()
    this.say("Restarted. Undo brings back the words you found.")
  }

  private undoWord(): void {
    if (!this.found.length && this.cleared) {
      this.found = this.cleared.found
      this.attempt = this.cleared.attempt
      this.cleared = null
      this.persist()
      this.renderState()
      return this.say("Your words are back.")
    }
    const w = this.found.pop()
    if (!w) return this.say("Nothing to undo.")
    this.undos++
    this.persist()
    this.renderState()
    this.say(`${w} is back on the list.`)
  }

  // ---- rendering -------------------------------------------------------------------------------
  private letter([r, c]: [number, number]): string { return this.p.rows[r]![c]! }

  private focusCell(cell: [number, number]): void {
    this.focus = cell
    this.table.querySelectorAll<HTMLElement>("td[data-r]").forEach((td) => {
      td.tabIndex = Number(td.dataset.r) === cell[0] && Number(td.dataset.c) === cell[1] ? 0 : -1
    })
    this.table.querySelector<HTMLElement>(`td[data-r="${cell[0]}"][data-c="${cell[1]}"]`)?.focus()
  }

  private rebuild(): void {
    const tbody = this.table.tBodies[0]!
    tbody.innerHTML = this.p.rows.map((row, r) => `<tr role="row">${[...row].map((ch, c) =>
      `<td role="gridcell" data-r="${r}" data-c="${c}" tabindex="${r === 0 && c === 0 ? 0 : -1}" aria-label="Row ${r + 1}, column ${c + 1}, letter ${ch}">${ch}</td>`).join("")}</tr>`).join("")
    this.table.style.setProperty("--ws-n", String(this.p.size))
    const list = this.root.querySelector<HTMLElement>("[data-words]")!
    list.innerHTML = this.p.words.map((w) => `<li data-word="${w.w}">${w.w}</li>`).join("")
    const theme = this.root.querySelector<HTMLElement>("[data-theme-name]")
    if (theme) theme.textContent = `Theme: ${this.p.theme}`
    document.querySelectorAll<HTMLElement>("[data-ws-levels] [data-level]").forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.level === this.p.level)))
    this.renderState()
  }

  private renderState(): void {
    gameProgress({ key: this.saveKey(this.p.level), ruleset: `word-search:${this.p.level}`, id: `${this.p.id}#${this.attempt}`, started: this.found.length > 0 || this.tried, won: this.found.length === this.p.words.length })
    const found = new Set(this.found)
    this.root.querySelectorAll<HTMLElement>("[data-words] li").forEach((li) => {
      const isFound = found.has(li.dataset.word ?? "")
      li.classList.toggle("found", isFound)
      li.setAttribute("aria-label", `${li.dataset.word}${isFound ? ", found" : ""}`)
    })
    const foundCells = new Set<string>()
    for (const w of this.p.words) if (found.has(w.w)) for (let k = 0; k < w.w.length; k++) foundCells.add(`${w.r + w.dr * k},${w.c + w.dc * k}`)
    const hint = this.hinted ? this.p.words.find((w) => w.w === this.hinted) : undefined
    this.table.querySelectorAll<HTMLElement>("td[data-r]").forEach((td) => {
      const key = `${td.dataset.r},${td.dataset.c}`
      td.classList.toggle("in-found", foundCells.has(key))
      td.classList.toggle("is-hint", !!hint && Number(td.dataset.r) === hint.r && Number(td.dataset.c) === hint.c)
    })
    this.drawOverlay()
  }

  private preview(from: [number, number] | null, to: [number, number] | null): void {
    const cells = from && to ? lineBetween(this.p.size, from[0], from[1], ...snapEnd(this.p.size, from[0], from[1], to[0], to[1])) : null
    const set = new Set((cells ?? []).map(([r, c]) => `${r},${c}`))
    this.table.querySelectorAll<HTMLElement>("td[data-r]").forEach((td) => td.classList.toggle("is-selecting", set.has(`${td.dataset.r},${td.dataset.c}`)))
  }

  private drawOverlay(): void {
    const n = this.p.size
    this.overlay.setAttribute("viewBox", `0 0 ${n * 100} ${n * 100}`)
    const found = this.p.words.filter((w: Placement) => this.found.includes(w.w))
    this.overlay.innerHTML = found.map((w) => {
      const x1 = w.c * 100 + 50, y1 = w.r * 100 + 50
      const x2 = (w.c + w.dc * (w.w.length - 1)) * 100 + 50, y2 = (w.r + w.dr * (w.w.length - 1)) * 100 + 50
      return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" />`
    }).join("")
  }

  private say(msg: string): void { if (this.status) this.status.textContent = msg }
}

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)
function readPref(): WLevel | null {
  try {
    const v = window.localStorage.getItem(PREF_KEY)
    return v === "easy" || v === "medium" || v === "hard" ? v : null
  } catch { return null }
}
function writePref(l: WLevel): void {
  try { window.localStorage.setItem(PREF_KEY, l) } catch { /* ignore */ }
}
